import { useContext, useRef, useState } from "react";
import VentasService from "../api/ventas.api";
import Toasts from "../../../components/notificacion/Toasts";
import { StateContext } from "../../../components/context/StateProvider";
import { getErrorMessage } from "../../../utils/ErrorUtils";

/**
 * Hook para forzar como preparado un pedido colgado de Nave 2 y sacarlo del listado.
 *
 * DECISION: se registra como PREPARADO con el flag `forzado` (ver
 * VentasService.forzarPreparadoN2): el listado de Nave 2 excluye los pedidos preparados,
 * así que desaparece, y el backend no manda al preparador de Nave 1 el aviso de "ya está
 * preparado". Para deshacerlo vale el "Recuperar pedido" de siempre.
 *
 * @param {Function} onForzado - Recibe el pedido forzado, para quitarlo de la lista local
 * @returns {{ pedidoAForzar: Object|null, forzando: boolean, solicitarForzado: Function,
 *   cancelarForzado: Function, confirmarForzado: Function }}
 */
const useForzarPedidoN2 = (onForzado) => {
    const { state, updateState } = useContext(StateContext);

    // Los toasts se acumulan sobre los actuales, que pueden haber cambiado mientras el
    // modal estaba abierto
    const estadoRef = useRef(state);
    estadoRef.current = state;

    const [pedidoAForzar, setPedidoAForzar] = useState(null);
    const [forzando, setForzando] = useState(false);

    const notificar = (mensaje, tipo) => {
        const toast = Toasts.generateToast(mensaje, tipo);
        updateState({ toasts: [...estadoRef.current.toasts, toast] });
    };

    const solicitarForzado = (pedido) => setPedidoAForzar(pedido);

    const cancelarForzado = () => setPedidoAForzar(null);

    const confirmarForzado = async () => {
        if (!pedidoAForzar) return;

        setForzando(true);
        try {
            await VentasService.forzarPreparadoN2(
                pedidoAForzar.num,
                estadoRef.current.year,
                pedidoAForzar.type
            );
            notificar(`Pedido ${pedidoAForzar.num} marcado como preparado`, 'success');
            onForzado(pedidoAForzar);
            setPedidoAForzar(null);
        } catch (error) {
            notificar(getErrorMessage(error, 'Error al forzar el pedido'), 'error');
        } finally {
            setForzando(false);
        }
    };

    return { pedidoAForzar, forzando, solicitarForzado, cancelarForzado, confirmarForzado };
};

export default useForzarPedidoN2;
